import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type { EmailDraftResponse, EmailImportance } from '@erp/rpc';

/**
 * Fetch a single email draft by ID.
 * Used when the EmailDraftSheet opens to get the full draft content.
 */
export function useEmailDraft(
  draftId: string | undefined,
  options?: { enabled?: boolean },
) {
  const query = useQuery({
    ...orpc.emailDrafts.get.queryOptions({
      input: draftId ? { draftId } : skipToken,
    }),
    enabled: Boolean(draftId) && (options?.enabled ?? true),
  });

  return {
    ...query,
    draft: query.data?.draft ?? null,
  };
}

/**
 * Update a draft (save edits) then send it.
 * This is the "update-then-send" pattern — the user just clicks "Send"
 * and we handle saving any edits as an implementation detail.
 */
export function useSendEmailDraft() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const updateMutation = useMutation({
    ...orpc.emailDrafts.update.mutationOptions(),
  });

  const sendMutation = useMutation({
    ...orpc.emailDrafts.send.mutationOptions(),
    onSuccess: () => {
      showToast({
        title: 'Email sent',
        message: 'The email draft has been sent successfully.',
        color: 'success',
      });
      // Invalidate both draft and suggested actions queries so the card disappears
      void queryClient.invalidateQueries({
        queryKey: orpc.emailDrafts.get.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.suggestedActions.listByEntity.key(),
      });
    },
    onError: (error: Error) => {
      showToast({
        title: 'Failed to send email',
        message: error.message,
        color: 'error',
      });
    },
  });

  /**
   * Save any edits then send the draft in one user action.
   */
  async function updateAndSend(
    draftId: string,
    edits: {
      to?: string;
      cc?: string;
      bcc?: string;
      subject?: string;
      htmlContent?: string;
      sendingMailbox?: string;
      documentAttachmentIds?: string[];
      importance?: EmailImportance;
    },
  ) {
    // Only call update if there are actual edits
    const hasEdits = Object.values(edits).some((v) => v !== undefined);
    if (hasEdits) {
      await updateMutation.mutateAsync({ draftId, ...edits });
    }
    await sendMutation.mutateAsync({ draftId });
  }

  return {
    updateAndSend,
    isPending: updateMutation.isPending || sendMutation.isPending,
  };
}

export type { EmailDraftResponse };
